import React from 'react';
import { Link } from 'react-router-dom';
import GeographySelectorForm from '../components/reports/GeographySelectorForm';
import InlinePDFViewer from '../components/reports/InlinePDFViewer';
import { useAuth } from '../hooks/useAuth';
import { ShieldCheckIcon, LockClosedIcon } from '@heroicons/react/24/outline';
import { useGeographyContext } from '../contexts/GeographyContext';

const SecurityPlanPage: React.FC = () => {
  const { isAuthenticated, profile } = useAuth();
  const {
    countries,
    states,
    lgas,
    selectedContinent,
    selectedCountry,
    selectedState,
    selectedLga,
    setSelectedContinent,
    setSelectedCountry,
    setSelectedState,
    setSelectedLga,
    loading,
    error,
  } = useGeographyContext();


  if (loading) {
    return <div className="flex justify-center items-center h-96"><p>Loading...</p></div>;
  }

  if (error) {
    return <div className="flex justify-center items-center h-96"><p>{error}</p></div>;
  }

  // Check subscription access - Gold or Platinum required for viewing
  const hasViewAccess = isAuthenticated && profile?.subscription_tier && 
    ['gold', 'platinum', 'diamond'].includes(profile.subscription_tier);
  
  // Only Diamond tier can download
  const hasDownloadAccess = isAuthenticated && profile?.subscription_tier && 
    profile.subscription_tier === 'diamond';

  // Show access restriction if user doesn't have Gold/Platinum
  if (!hasViewAccess) {
    return (
      <div className="h-full flex flex-col">
        <div className="mb-4">
          <h2 className="text-2xl font-bold text-gray-900">Security Plan</h2>
          <p className="text-gray-600 mt-1">Comprehensive security planning and recommendations</p>
        </div>

        <div className="flex-1 flex items-center justify-center">
          <div className="max-w-md text-center">
            <LockClosedIcon className="mx-auto h-16 w-16 text-gray-400" />
            <h3 className="mt-4 text-lg font-semibold text-gray-900">Subscription Required</h3>
            <p className="mt-2 text-gray-600">
              Access to Security Plan requires a Gold or Platinum subscription.
            </p>
            <div className="mt-6 space-y-3">
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-left">
                <div className="flex items-start gap-3">
                  <ShieldCheckIcon className="h-5 w-5 text-yellow-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-yellow-800">Gold Tier</p>
                    <p className="text-xs text-yellow-700 mt-1">View Security Plan reports</p>
                  </div>
                </div>
              </div>
              <div className="bg-blue-50 border border-blue-300 rounded-lg p-4 text-left">
                <div className="flex items-start gap-3">
                  <ShieldCheckIcon className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-blue-800">Diamond Tier</p>
                    <p className="text-xs text-blue-700 mt-1">View and download Security Plan reports</p>
                  </div>
                </div>
              </div>
            </div>
            <Link
              to="/subscription"
              className="mt-6 inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700"
            >
              View Subscription Plans
            </Link>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="h-full flex flex-col">
      <div className="mb-4">
        <h2 className="text-2xl font-bold text-gray-900">Security Plan</h2>
        <p className="text-gray-600 mt-1">Comprehensive security planning and recommendations</p>
        {!hasDownloadAccess && (
          <div className="mt-2 inline-flex items-center gap-2 px-3 py-1 bg-yellow-50 border border-yellow-200 rounded-md">
            <ShieldCheckIcon className="h-4 w-4 text-yellow-600" />
            <span className="text-xs text-yellow-700">View Only - Upgrade to Diamond for downloads</span>
          </div>
        )}
      </div>

      <GeographySelectorForm
        selectedContinent={selectedContinent}
        countries={countries}
        states={states}
        lgas={lgas}
        selectedCountry={selectedCountry}
        selectedState={selectedState}
        selectedLga={selectedLga}
        onContinentChange={setSelectedContinent}
        onCountryChange={setSelectedCountry}
        onStateChange={setSelectedState}
        onLgaChange={setSelectedLga}
      />

      <div className="mt-6">
        <InlinePDFViewer 
          reportType="security_plan"
          selectedCountry={countries.find(c => c.id === selectedCountry)}
          selectedState={states.find(s => s.id === selectedState)}
          selectedLga={lgas.find(l => l.id === selectedLga)}
          downloadTierRequired="diamond"
        />
      </div>
    </div>
  );
};

export default SecurityPlanPage;
